import React, { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { formatDateEST } from '../utils/dateUtils';


type ReportType = 'annual-summary' | 'highest-grossing-events' | 'highest-paying-customers';

const Reports: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [selectedReport, setSelectedReport] = useState<ReportType>('annual-summary');
  const [loading, setLoading] = useState(false);
  const [annualData, setAnnualData] = useState<any>(null);
  const [eventsData, setEventsData] = useState<any[]>([]);
  const [customersData, setCustomersData] = useState<any[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      if (selectedReport === 'annual-summary') {
        const res = await reportsAPI.getAnnualSummary();
        setAnnualData(res.data);
      } else if (selectedReport === 'highest-grossing-events') {
        const res = await reportsAPI.getHighestGrossingEvents();
        setEventsData(res.data.events || []);
      } else if (selectedReport === 'highest-paying-customers') {
        const res = await reportsAPI.getHighestPayingCustomers();
        setCustomersData(res.data.customers || []);
      }
    } catch (error) {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  }, [selectedReport]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    return formatDateEST(dateString);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-primary-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-primary-50 text-primary-700 rounded-lg shadow transition-all duration-200 hover:shadow-md border border-primary-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </Link>
          </div>
          <h1 className="text-4xl font-bold text-primary-700 mb-2">Reports</h1>
          <p className="text-secondary-600">Comprehensive business analytics and insights</p>
        </div>

        {/* Report Type Selector */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <label className="block text-sm font-semibold text-secondary-700 mb-3">Select Report Type</label>
          <select
            value={selectedReport}
            onChange={(e) => setSelectedReport(e.target.value as ReportType)}
            className="w-full md:w-96 px-4 py-3 border-2 border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg font-medium"
          >
            <option value="annual-summary">Annual Income/Expenditure Summary</option>
            <option value="highest-grossing-events">Highest Grossing Events (Top 10)</option>
            <option value="highest-paying-customers">Highest Paying Customers (Top 10)</option>
          </select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto"></div>
            <p className="text-secondary-600 mt-4 text-lg">Loading report...</p>
          </div>
        )}

        {/* Annual Summary Report */}
        {!loading && selectedReport === 'annual-summary' && annualData && (
          <div className="space-y-6">
            {/* Current Year */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-primary-700 mb-6">{annualData.currentYear.year} Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-6">
                  <p className="text-sm font-semibold text-green-700 uppercase tracking-wide">Total Income</p>
                  <p className="text-3xl font-bold text-green-900 mt-2">{formatCurrency(annualData.currentYear.totalIncome)}</p>
                  <div className="mt-3 space-y-1 text-sm text-green-700">
                    <p>Ticket Sales: {formatCurrency(annualData.currentYear.ticketSales)}</p>
                    <p>Other Income: {formatCurrency(annualData.currentYear.otherIncome)}</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-300 rounded-xl p-6">
                  <p className="text-sm font-semibold text-red-700 uppercase tracking-wide">Total Expenditure</p>
                  <p className="text-3xl font-bold text-red-900 mt-2">{formatCurrency(annualData.currentYear.totalExpenditure)}</p>
                </div>
                
                <div className={`bg-gradient-to-br ${annualData.currentYear.netProfit >= 0 ? 'from-blue-50 to-blue-100 border-blue-300' : 'from-orange-50 to-orange-100 border-orange-300'} border-2 rounded-xl p-6`}>
                  <p className={`text-sm font-semibold uppercase tracking-wide ${annualData.currentYear.netProfit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                    Net {annualData.currentYear.netProfit >= 0 ? 'Profit' : 'Loss'}
                  </p>
                  <p className={`text-3xl font-bold mt-2 ${annualData.currentYear.netProfit >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
                    {formatCurrency(Math.abs(annualData.currentYear.netProfit))}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-primary-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-primary-700">Total Events</p>
                  <p className="text-2xl font-bold text-primary-900">{annualData.currentYear.totalEvents}</p>
                </div>
                <div className="bg-primary-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-primary-700">Total Customers</p>
                  <p className="text-2xl font-bold text-primary-900">{annualData.currentYear.totalCustomers}</p>
                </div>
              </div>
            </div>

            {/* Previous Year */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-primary-700 mb-6">{annualData.previousYear.year} Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-6">
                  <p className="text-sm font-semibold text-green-700 uppercase tracking-wide">Total Income</p>
                  <p className="text-3xl font-bold text-green-900 mt-2">{formatCurrency(annualData.previousYear.totalIncome)}</p>
                  <div className="mt-3 space-y-1 text-sm text-green-700">
                    <p>Ticket Sales: {formatCurrency(annualData.previousYear.ticketSales)}</p>
                    <p>Other Income: {formatCurrency(annualData.previousYear.otherIncome)}</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-300 rounded-xl p-6">
                  <p className="text-sm font-semibold text-red-700 uppercase tracking-wide">Total Expenditure</p>
                  <p className="text-3xl font-bold text-red-900 mt-2">{formatCurrency(annualData.previousYear.totalExpenditure)}</p>
                </div>
                
                <div className={`bg-gradient-to-br ${annualData.previousYear.netProfit >= 0 ? 'from-blue-50 to-blue-100 border-blue-300' : 'from-orange-50 to-orange-100 border-orange-300'} border-2 rounded-xl p-6`}>
                  <p className={`text-sm font-semibold uppercase tracking-wide ${annualData.previousYear.netProfit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                    Net {annualData.previousYear.netProfit >= 0 ? 'Profit' : 'Loss'}
                  </p>
                  <p className={`text-3xl font-bold mt-2 ${annualData.previousYear.netProfit >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
                    {formatCurrency(Math.abs(annualData.previousYear.netProfit))}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-primary-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-primary-700">Total Events</p>
                  <p className="text-2xl font-bold text-primary-900">{annualData.previousYear.totalEvents}</p>
                </div>
                <div className="bg-primary-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-primary-700">Total Customers</p>
                  <p className="text-2xl font-bold text-primary-900">{annualData.previousYear.totalCustomers}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Highest Grossing Events Report */}
        {!loading && selectedReport === 'highest-grossing-events' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-6 bg-gradient-to-r from-primary-600 to-secondary-600">
              <h2 className="text-2xl font-bold text-white">Top 10 Highest Grossing Events</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-primary-200">
                <thead className="bg-primary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Event Title</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Event Type</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-primary-700 uppercase tracking-wider">Revenue</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-primary-700 uppercase tracking-wider">Customers</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-primary-700 uppercase tracking-wider">Tickets Sold</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-primary-100">
                  {eventsData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-secondary-400">No events found</td>
                    </tr>
                  ) : (
                    eventsData.map((event, index) => (
                      <tr key={event.uuid} className="hover:bg-primary-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-white ${
                            index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-600' : 'bg-primary-600'
                          }`}>
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-secondary-900">{event.title}</td>
                        <td className="px-6 py-4 text-secondary-700">{event.eventType}</td>
                        <td className="px-6 py-4 text-secondary-700">{formatDate(event.eventStartDate)}</td>
                        <td className="px-6 py-4 text-right font-bold text-green-700">{formatCurrency(event.totalRevenue)}</td>
                        <td className="px-6 py-4 text-center text-secondary-700">{event.totalCustomers}</td>
                        <td className="px-6 py-4 text-center text-secondary-700">{event.totalTicketsSold}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Highest Paying Customers Report */}
        {!loading && selectedReport === 'highest-paying-customers' && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-6 bg-gradient-to-r from-primary-600 to-secondary-600">
              <h2 className="text-2xl font-bold text-white">Top 10 Highest Paying Customers</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-primary-200">
                <thead className="bg-primary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Customer Name</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-primary-700 uppercase tracking-wider">Total Spent</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-primary-700 uppercase tracking-wider">Events Attended</th>
                    <th className="px-6 py-3 text-center text-xs font-bold text-primary-700 uppercase tracking-wider">Tickets Purchased</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-primary-100">
                  {customersData.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-secondary-400">No customers found</td>
                    </tr>
                  ) : (
                    customersData.map((customer, index) => (
                      <tr key={customer.uuid} className="hover:bg-primary-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-white ${
                            index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-600' : 'bg-primary-600'
                          }`}>
                            {index + 1}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-secondary-900">{customer.customerName}</td>
                        <td className="px-6 py-4 text-secondary-700">{customer.email}</td>
                        <td className="px-6 py-4 text-right font-bold text-green-700">{formatCurrency(customer.totalSpent)}</td>
                        <td className="px-6 py-4 text-center text-secondary-700">{customer.eventsAttended}</td>
                        <td className="px-6 py-4 text-center text-secondary-700">{customer.totalTicketsPurchased}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
